/**
 * `ThemeToggle` — round 36px button that flips light/dark theme and
 * previews the other side on hover.
 *
 * Visual grammar:
 *   - Resting glyph = the OPPOSITE of the active theme, i.e. dark mode
 *     shows the sun (where you'd go), light mode shows the moon. Same
 *     metaphor used by Vercel, Linear, GitHub.
 *   - Hover: glyph nudges + rotates slightly via framer-motion to telegraph
 *     interactivity. Sun has rays that fan out; moon has stars that drift.
 *   - Click: glyphs cross-fade with a small scale/rotate stagger
 *     (`AnimatePresence mode="wait"`).
 *
 * Accessibility: `aria-pressed` reflects dark state; `aria-label` names the
 * destination action ("Switch to dark theme" / "Switch to light theme"),
 * which is what screen-reader users expect for a toggle.
 */
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { useTheme } from '../lib/use-theme';

const SUN_RAYS: readonly { x1: number; y1: number; x2: number; y2: number }[] = [
  { x1: 12, y1: 2, x2: 12, y2: 4 },
  { x1: 12, y1: 20, x2: 12, y2: 22 },
  { x1: 2, y1: 12, x2: 4, y2: 12 },
  { x1: 20, y1: 12, x2: 22, y2: 12 },
  { x1: 4.9, y1: 4.9, x2: 6.3, y2: 6.3 },
  { x1: 17.7, y1: 17.7, x2: 19.1, y2: 19.1 },
  { x1: 4.9, y1: 19.1, x2: 6.3, y2: 17.7 },
  { x1: 17.7, y1: 6.3, x2: 19.1, y2: 4.9 },
];

const MOON_STARS: readonly { cx: number; cy: number; r: number }[] = [
  { cx: 18.5, cy: 5.5, r: 0.6 },
  { cx: 5.2, cy: 4.4, r: 0.5 },
  { cx: 20, cy: 14, r: 0.45 },
];

function Sun({ hovered }: { hovered: boolean }) {
  return (
    <motion.svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-[18px] w-[18px]"
      initial={{ rotate: -25, scale: 0.85, opacity: 0 }}
      animate={{
        rotate: hovered ? 12 : 0,
        scale: 1,
        opacity: 1,
      }}
      exit={{ rotate: 25, scale: 0.85, opacity: 0 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
    >
      <motion.circle
        cx={12}
        cy={12}
        r={4}
        fill="currentColor"
        stroke="none"
        animate={{ scale: hovered ? 1.08 : 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 20 }}
        style={{ transformOrigin: '12px 12px' }}
      />
      {SUN_RAYS.map((ray, idx) => (
        <motion.line
          key={`ray-${String(idx)}`}
          x1={ray.x1}
          y1={ray.y1}
          x2={ray.x2}
          y2={ray.y2}
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{
            pathLength: 1,
            opacity: 1,
            scale: hovered ? 1.18 : 1,
          }}
          transition={{
            delay: 0.04 + idx * 0.02,
            duration: 0.32,
            type: 'spring',
            stiffness: 260,
            damping: 22,
          }}
          style={{ transformOrigin: '12px 12px' }}
        />
      ))}
    </motion.svg>
  );
}

function Moon({ hovered }: { hovered: boolean }) {
  return (
    <motion.svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-[18px] w-[18px]"
      initial={{ rotate: 25, scale: 0.85, opacity: 0 }}
      animate={{
        rotate: hovered ? -14 : 0,
        scale: 1,
        opacity: 1,
      }}
      exit={{ rotate: -25, scale: 0.85, opacity: 0 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
    >
      <motion.path
        d="M20.5 14.5A8.5 8.5 0 0 1 9.5 3.5a8.5 8.5 0 1 0 11 11Z"
        fill="currentColor"
        stroke="currentColor"
        animate={{
          scale: hovered ? 1.05 : 1,
        }}
        transition={{ type: 'spring', stiffness: 260, damping: 20 }}
        style={{ transformOrigin: '12px 12px' }}
      />
      {MOON_STARS.map((star, idx) => (
        <motion.circle
          key={`star-${String(idx)}`}
          cx={star.cx}
          cy={star.cy}
          r={star.r}
          fill="currentColor"
          stroke="none"
          initial={{ opacity: 0, scale: 0 }}
          animate={{
            opacity: hovered ? 1 : 0.55,
            scale: hovered ? 1.25 : 1,
            y: hovered ? -1 : 0,
          }}
          transition={{
            delay: 0.05 + idx * 0.04,
            type: 'spring',
            stiffness: 240,
            damping: 18,
          }}
        />
      ))}
    </motion.svg>
  );
}

export function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  const [hovered, setHovered] = useState(false);
  const isDark = theme === 'dark';
  // Show the destination glyph — clicking goes there. Dark active → sun
  // (action: switch to light); light active → moon (action: switch to dark).
  const showSun = isDark;
  const label = isDark ? 'Switch to light theme' : 'Switch to dark theme';

  return (
    <motion.button
      type="button"
      onClick={toggleTheme}
      onMouseEnter={() => {
        setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
      onFocus={() => {
        setHovered(true);
      }}
      onBlur={() => {
        setHovered(false);
      }}
      aria-label={label}
      aria-pressed={isDark}
      title={label}
      whileTap={{ scale: 0.92 }}
      transition={{ type: 'spring', stiffness: 380, damping: 22 }}
      className={clsx(
        'relative inline-flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden',
        'rounded-md border border-line-strong bg-paper-1 text-ink-2 shadow-soft',
        // Animate ONLY border + background. `color` must snap so the SVG
        // (which inherits via `currentColor`) doesn't drag through a
        // 150ms tween when the theme flips — the user sees the icon
        // "stuck white" until the transition completes.
        'transition-[border-color,background-color] duration-150',
        'hover:border-ton-blue-d hover:text-ton-blue hover:bg-[rgb(var(--paper-1-hover))]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ton-blue/40',
      )}
    >
      {/* No `mode="wait"` — let outgoing icon fade while incoming starts.
          Both glyphs are absolutely positioned in the same slot so they
          cross-fade in place. Without this, the spring exit holds the
          button visually empty for ~400ms. */}
      <AnimatePresence initial={false}>
        {showSun ? (
          <motion.span
            key="sun"
            className="absolute inset-0 flex items-center justify-center"
          >
            <Sun hovered={hovered} />
          </motion.span>
        ) : (
          <motion.span
            key="moon"
            className="absolute inset-0 flex items-center justify-center"
          >
            <Moon hovered={hovered} />
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  );
}
